import { Table } from 'antd';
import Column from 'antd/lib/table/Column';
import { useMemo } from 'react';

import FilterIcon from '../../../components/Icons/FilterIcon';
import { DocumentMedicalBillEntity } from '../../../generated-api';
import convertCSVColumns from '../../../utils/convertCSVColumns';
import ensureDefined from '../../../utils/ensureDefined';
import ocrTypeNameDefinitions from '../../../utils/ocrTypeNameDefinitions';
import FilterDropdown from '../FilterDropdown';
import { convertToExcelForTatsujin } from '../MedicalBill/convertToExcelForTatsujin';
import { convertToMedicalExpenseSummaryForm } from '../MedicalBill/convertToMedicalExpenseSummaryForm';
import { MedicalBillFormInputs } from '../MedicalBill/MedicalBillFormInputs';
import { medicalBillStatsGrouping } from '../MedicalBill/medicalBillStatsGrouping';
import { MedicalBillStatsEntity } from '../MedicalBill/types';
import useResultFilter from '../useResultFilter';
import renderTableColumn from './renderTableColumn';
import {
  CSVConvertResult,
  OcrResultPageDefinition,
  OcrResultPageTableComponentProps,
  ResultPageDocument,
} from './types';

function MedicalBillStatsTable({
  loading,
  results,
  tabIndex,
}: OcrResultPageTableComponentProps<DocumentMedicalBillEntity>) {
  const record = useMemo(() => medicalBillStatsGrouping(results), [results]);
  const { applyFilter, filterItems, filteredResults } = useResultFilter(
    record,
    medicalBillResultPageDefinition,
    tabIndex
  );

  const createFilterDropDown = (dataIndex: string) => ({
    filterDropdown:
      filterItems[tabIndex][dataIndex] != null ? (
        <FilterDropdown onApply={(v) => applyFilter(tabIndex, [dataIndex], v)} />
      ) : null,
    filterIcon: () => <FilterIcon enabled={!!filterItems[tabIndex][dataIndex]} />,
  });

  return (
    <Table
      dataSource={filteredResults}
      loading={loading}
      pagination={{ defaultPageSize: 20, showSizeChanger: true, position: ['topRight', 'bottomRight'] }}>
      <Column dataIndex="patientName" {...createFilterDropDown('patientName')} title="受診者名" />
      <Column dataIndex="hospitalName" {...createFilterDropDown('hospitalName')} title="医療機関名" />
      <Column dataIndex="address" {...createFilterDropDown('address')} title="住所" />
      <Column dataIndex="totalAmount" sorter={(a: number, b: number) => a - b} title="合計金額" />
    </Table>
  );
}

const medicalBillResultPageDefinition: OcrResultPageDefinition<DocumentMedicalBillEntity> = {
  name: ocrTypeNameDefinitions.medical_bill,
  formComponent: MedicalBillFormInputs,
  tabs: [
    {
      name: '詳細',
      csvColumns: {
        ファイル名: ['parsed_path', 'fullFileName'],
        ページ数: ['page'],
        日付: ['latest_revision', 'date'],
        金額: ['latest_revision', 'amount'],
        住所: ['latest_revision', 'address'],
        医療機関名: ['latest_revision', 'hospital_name'],
        受診者名: ['latest_revision', 'patient_name'],
      },
      tableColumns: [
        { dataIndex: ['latest_revision', 'date'], title: '日付', render: renderTableColumn },
        { dataIndex: ['latest_revision', 'amount'], title: '金額', render: renderTableColumn, sort: true },
        { dataIndex: ['latest_revision', 'address'], title: '住所', render: renderTableColumn, searchFilter: true },
        {
          dataIndex: ['latest_revision', 'hospital_name'],
          title: '医療機関名',
          render: renderTableColumn,
          searchFilter: true,
        },
        {
          dataIndex: ['latest_revision', 'patient_name'],
          title: '受診者名',
          render: renderTableColumn,
          searchFilter: true,
        },
      ],
    },
    {
      name: '集計',
      csvColumns: {
        ファイル名: ['fileName'],
        受診者: ['patientName'],
        医療機関名: ['hospitalName'],
        住所: ['address'],
        合計金額: ['totalAmount'],
      },
      tableColumns: [
        { dataIndex: ['patientName'], title: '受診者名', render: renderTableColumn, searchFilter: true },
        { dataIndex: ['hospitalName'], title: '医療機関名', render: renderTableColumn, searchFilter: true },
        { dataIndex: ['address'], title: '住所', render: renderTableColumn, searchFilter: true },
        { dataIndex: ['totalAmount'], title: '合計金額', render: renderTableColumn, sort: true },
      ],
      tableComponent: MedicalBillStatsTable,
    },
  ],
  csvDataConverters: [
    {
      labelName: '詳細',
    },
    {
      labelName: '集計',
      converter: (values: ResultPageDocument<DocumentMedicalBillEntity>[]) => {
        // convertCSVColumns に渡す csvColumns が必要なので引っ張ってくる
        // FIXME: そもそも DownloadCsvButton のほうで convertCSVColumns を通す流れにすれば必要ないはず
        const { csvColumns } = medicalBillResultPageDefinition.tabs[1];
        const result = medicalBillStatsGrouping(values);
        return {
          fileNameSuffix: '集計',
          csv: convertCSVColumns(
            result.map<MedicalBillStatsEntity>((row) => ({
              fileName: row.fileName,
              patientName: row.patientName,
              hospitalName: row.hospitalName,
              address: row.address,
              totalAmount: row.totalAmount,
            })),
            csvColumns
          ),
        };
      },
    },
    {
      labelName: '両方',
      converter: (values: ResultPageDocument<DocumentMedicalBillEntity>[]) => {
        // convertCSVColumns に渡す csvColumns が必要なので引っ張ってくる
        // FIXME: そもそも DownloadCsvButton のほうで convertCSVColumns を通す流れにすれば必要ないはず
        const { csvColumns } = medicalBillResultPageDefinition.tabs[0];
        const statConverter = ensureDefined(medicalBillResultPageDefinition.csvDataConverters?.[1].converter);
        return [
          {
            fileNameSuffix: '',
            csv: convertCSVColumns(values, csvColumns),
          },
          statConverter(medicalBillStatsGrouping(values)) as CSVConvertResult,
        ];
      },
    },
  ],
  excelDataConverters: [
    {
      labelName: '達人インポートフォーマット',
      converter: convertToExcelForTatsujin,
    },
    {
      labelName: '医療費集計フォーム',
      converter: convertToMedicalExpenseSummaryForm,
    },
  ],
};
export default medicalBillResultPageDefinition;
